import React from 'react';
import { sendRequest, API_ROOT } from '../../supplementary_components/requests';
import { priceOnFinish } from '../../supplementary_components/priceInputDelegate';
import { PiSplitHorizontalFill } from "react-icons/pi";
import styles from './reports.module.scss';
import { MdEdit } from "react-icons/md";
import { addPopup, addNotification } from '../../supplementary_components/store';
import { CustomAddForm } from '../../general_components/forms/CustomAddForm';

import { TotalGrossContainer, RPMContainer, WeekSelectionWrapper, dateToLocalDateString} from './weeklyReports'
import * as XLSX from "xlsx";
import { extractStateStr } from '../../main_board_components/unassignedLoadsTable';
import { PopupOnClick } from '../../general_components/content_management/reusableItems/itemOnPopup';
import { LuRefreshCcw } from "react-icons/lu";
import { can } from '../../supplementary_components/userDataDelegate';
import { accumulateWeeklyGrossAndMiles } from '../../main_board_components/plannerBoardSettings';
import { TRUCK_PERFORMANCE_COLUMNS, TRUCK_PERFORMANCE_KEY, TRUCK_PERFORMANCE_LOAD_COLUMNS, TRUCK_PERFORMANCE_LOADS_KEY, getVisibleReportColumns, isLegacyTruckLoadColumns, orderKeysByCatalog, resolveVisibleCatalog } from './reportColumnSettings';

const payoutGrossOf = (report) => {
    const loads = Array.isArray(report?.loads) ? report.loads : [];
    return loads.reduce((sum, load) => sum + (Number(load?.driverPayoutForThisWeek) || 0), 0);
};

const loadMileTotalsOf = (report) => {
    const loads = Array.isArray(report?.loads) ? report.loads : [];
    if (loads.length === 0) return null;
    return accumulateWeeklyGrossAndMiles(loads.map((load) => ({
        ...load,
        amountForInvoice: load?.payoutForThisWeek,
    })));
};

const payoutLoadRpmOf = (report) => {
    const totals = loadMileTotalsOf(report);
    if (!totals || totals.loadWithoutMileage || totals.totalMiles === 0) return null;
    return payoutGrossOf(report) / totals.totalMiles;
};

const odometerMilesOf = (report) => (Number(report?.coveredMileage) || 0) + (Number(report?.adjustmentAmount) || 0);

const payoutOdoRpmOf = (report) => {
    const miles = odometerMilesOf(report);
    if (!miles) return null;
    return payoutGrossOf(report) / miles;
};

const grossRpmOf = (report) => {
    const miles = odometerMilesOf(report);
    if (!miles) return null;
    return (Number(report?.totalGross) || 0) / miles;
};

const cellClass = (key, isLast, extra) => [styles[key], extra, isLast ? styles.lastChild : ''].filter(Boolean).join(' ');

export const AllTrucksReportByWeeks = ({companyData, department}) => {
    const headerObject = [
        {
            label: "Truck Number",
            key: "truckNumber",
            sortable: true
        }, 
        {
            label: "Total Gross",
            key: "totalGross",
            sortable: true,
        }, 
        {
            label: "Odometer Miles",
            key: "mileage",
            sortable: true,
            methodToObtainRawVal : odometerMilesOf,
        },
        {
            label: "RPM",
            key: "rpm",
            sortable: true,
            methodToObtainRawVal : grossRpmOf,
        },
        {
            label: "Payout Gross",
            key: "payoutGross",
            sortable: true,
            methodToObtainRawVal: payoutGrossOf,
        },
        {
            label: "Payout/Load RPM",
            key: "payoutLoadRpm",
            sortable: true,
            methodToObtainRawVal: payoutLoadRpmOf,
        },
        {
            label: "Payout/Odo RPM",
            key: "payoutOdoRpm",
            sortable: true,
            methodToObtainRawVal: payoutOdoRpmOf,
        },
        {
            label: "Active Days",
            key: "activeDays",
            sortable: true
        },
        {
            label: "Truck Type",
            key: "truckType",
            sortable: true,
            methodToObtainRawVal : (report) => {
                return Array.isArray(report.truckType) ? report.truckType.join(', ') : report.truckType;
            }
        },
        {
            label: "Available Days",
            key: "availableDays",
            sortable: true
        },
        {
            label: "Load Count",
            key: "loadCount",
            sortable: true,
            methodToObtainRawVal : (report) => {
                return report.loads?.length || 0;
            }
        },
    ]
    const customAction = {
        label: <>Reload Mileage<LuRefreshCcw /></>,

        analyzeDataToShow: (data) => {
            return Array.isArray(data) && data.some(item => item?.weeklyRecordId);
        },
        onClick: ({startDate, setData}) => {
            return new Promise((resolve) => {
                sendRequest('POST', `${API_ROOT}trucks/refreshWeeklyMileage?startDate=${dateToLocalDateString(startDate)}`, null, (updates) => {
                    const mileageByTruck = (updates || []).reduce((acc, entry) => Object.assign(acc, entry), {});
                    setData(prev => prev.map(report => (
                        Object.prototype.hasOwnProperty.call(mileageByTruck, report.truckNumber)
                            ? {...report, coveredMileage: mileageByTruck[report.truckNumber]}
                            : report
                    )));
                    resolve();
                }, () => {
                    addNotification({message:`Failed to refresh mileage`, type:'alert'});
                    resolve();
                }, null);
            });
        },
        
    }
    return <WeekSelectionWrapper companyData={companyData} reportLink={'trucks/allTrucksGrossesForSelectedWeek'} rowType={"clickable"} headerObject={headerObject} department={department}  custoMreportClass={WeeklyTruckGrossReport} rowFunction={TruckRowReport} SubContentPart={SubContentPart} subcontentArrayName={"loads"} exportToXlsx={exportTruckLoadsReportGroupedToXlsx}  startWithPrevWeek={true} sortingAllowed={true} customAction={customAction} columnSettings={{
        reportKey: TRUCK_PERFORMANCE_KEY,
        catalog: TRUCK_PERFORMANCE_COLUMNS,
        target: 'parent',
        sectionTitle: 'Columns',
        sectionHelp: 'Choose which columns appear on this report. Truck Number stays visible.',
        loadColumns: {
            reportKey: TRUCK_PERFORMANCE_LOADS_KEY,
            catalog: TRUCK_PERFORMANCE_LOAD_COLUMNS,
            sectionTitle: 'Expanded loads',
            sectionHelp: 'Choose which fields appear when a truck row is expanded. Load # stays visible. Leave this unchanged to keep the current layout.',
        },
    }} />
    
}

const TruckRowReport = (key, singleReport, currentReport, selectedPeriod, setCurrentReport, department, isLast )=>{

    if(key === "totalGross"){
        return <div key={key} className={cellClass(null, isLast, styles.hasCustomColor)}><TotalGrossContainer  totalGross={currentReport.totalGross} /></div>
    }else if(key === "rpm"){
        return <div key={key} className={cellClass(null, isLast, singleReport.rpm !== '-' ? styles.hasCustomColor : '')}><RPMContainer  rpm={singleReport.rpm} /></div>
    }else if(key === "payoutGross"){
        return <div key={key} className={cellClass(null, isLast, styles.hasCustomColor)}><TotalGrossContainer totalGross={payoutGrossOf(currentReport)} /></div>
    }else if(key === "payoutLoadRpm"){
        const rpm = payoutLoadRpmOf(currentReport);
        if (rpm == null) return <div key={key} className={cellClass(null, isLast)}>N/A</div>;
        return <div key={key} className={cellClass(null, isLast, styles.hasCustomColor)}><RPMContainer rpm={rpm.toFixed(2)} /></div>
    }else if(key === "payoutOdoRpm"){
        const rpm = payoutOdoRpmOf(currentReport);
        if (rpm == null) return <div key={key} className={cellClass(null, isLast)}>-</div>;
        return <div key={key} className={cellClass(null, isLast, styles.hasCustomColor)}><RPMContainer rpm={rpm.toFixed(2)} /></div>
    }else if(key === "mileage"){
        return <div key={key} className={cellClass(key, isLast)} >
            <>
            {singleReport[key] ? `${singleReport[key]}` : "-"}
            {singleReport.adjustment ? <span>({singleReport.adjustment})</span> : null}
                {can('PERFORMANCE_REPORTS', 'MANAGE') &&  currentReport.weeklyRecordId ? <MdEdit onClick={(e)=>{
                    e.preventDefault();
                    e.stopPropagation();
                    updateWeeklyTruckRecord(currentReport, selectedPeriod, setCurrentReport, "adjustmentAmount");
                }} />  : null}
            </>
            
        </div>
    }else if(key === "adjustment"){
        return null;
    }else if(key === "availableDays" || key === "activeDays"){
        return <div key={key} className={cellClass(key, isLast)} >
            <>
                {singleReport[key] ?? "-"}
                {can('PERFORMANCE_REPORTS', 'MANAGE') &&  currentReport.weeklyRecordId ? <MdEdit onClick={(e)=>{
                    e.preventDefault();
                    e.stopPropagation();
                    updateWeeklyTruckRecord(currentReport, selectedPeriod, setCurrentReport, key);
                }} />  : null}
            </>
        </div>
    }
    return <div key={key} className={cellClass(key, isLast)}>{singleReport[key]}</div>;
}
const loadIsCancelled = (load) => load?.loadStatus === 'Cancelled' || load?.TONU;

const truckLoadMiles = (load) => {
    const loaded = Number(load?.mileage) || 0;
    const deadhead = Number(load?.deadhead) || 0;
    if (loadIsCancelled(load)) {
        return { text: deadhead.toLocaleString('en-US'), deadheadOnly: true, exportValue: deadhead };
    }
    if (loaded === 0) {
        return { text: 'N/A', deadheadOnly: false, exportValue: 'N/A' };
    }
    const total = loaded + deadhead;
    return { text: total.toLocaleString('en-US'), deadheadOnly: false, exportValue: total };
};

const LoadNumberCell = ({ load }) => (
    <div className={styles.loadNumberCell} onClick={(e) => e.stopPropagation()}>
        <PopupOnClick text={load.loadNumber} itemType="load" itemId={load.loadId} />
        {loadIsCancelled(load) ? <span className={styles.loadBadgeCancelled}>Cancelled</span> : null}
    </div>
);

const LoadMilesCell = ({ load }) => {
    const miles = truckLoadMiles(load);
    return (
        <div className={styles.loadMilesCell}>
            <span>{miles.text}</span>
            {miles.deadheadOnly ? <span className={styles.loadBadgeDeadhead}>Deadhead</span> : null}
        </div>
    );
};

const LoadRateCell = ({ load }) => (
    <div className={styles.truckGrossLoadCardRate}>
        {load?.rateDistributedBetweenWeeks ? <PiSplitHorizontalFill /> : null}
        {priceOnFinish(`${load?.payoutForThisWeek ?? 0}`, true)}
    </div>
);

const renderTruckLoadCell = (load, column) => {
    if (column.key === 'loadNumber') return <LoadNumberCell load={load} />;
    if (column.key === 'states') {
        return <div className={styles.fromToStates}>{`${extractStateStr(load.startCityState)} - ${extractStateStr(load.endCityState)}`}</div>;
    }
    if (column.key === 'miles') return <LoadMilesCell load={load} />;
    if (column.key === 'payout') return <div>{priceOnFinish(`${load?.driverPayoutForThisWeek ?? 0}`, true)}</div>;
    if (column.key === 'rate') return <LoadRateCell load={load} />;
    return <div />;
};

const SubContentPart = ({ currentReport, loadVisibleColumns, loadCatalog, customLoadLayout }) => {
    const loads = Array.isArray(currentReport?.loads) ? currentReport.loads : [];
    const catalog = loadCatalog || TRUCK_PERFORMANCE_LOAD_COLUMNS;
    const keys = orderKeysByCatalog(
        Array.isArray(loadVisibleColumns) && loadVisibleColumns.length
            ? loadVisibleColumns
            : getVisibleReportColumns(TRUCK_PERFORMANCE_LOADS_KEY, catalog),
        catalog
    );
    const columns = resolveVisibleCatalog(keys, catalog);
    const legacy = !customLoadLayout && isLegacyTruckLoadColumns(columns.map((col) => col.key));

    if (legacy) {
        return <>{loads.map((load, subIndex) => (
            <div key={load?.loadId ?? subIndex}>
                <LoadNumberCell load={load} />
                <div></div>
                <div className={styles.fromToStates}>{`${extractStateStr(load.startCityState)} - ${extractStateStr(load.endCityState)}`}</div>
                <LoadRateCell load={load} />
            </div>
        ))}</>;
    }

    const template = columns.map((col) => col.grid || 'minmax(6rem, 1fr)').join(' ');
    return (
        <div className={styles.loadSubTable} style={{ '--sub-col-template': template }}>
            <div className={`${styles.loadSubHeader} ${styles.truckLoadSubHeader}`}>
                {columns.map((col) => (
                    <div key={col.key} className={col.key === 'miles' ? styles.loadMilesColumn : undefined}>{col.label}</div>
                ))}
            </div>
            {loads.map((load, index) => (
                <div key={load?.loadId ?? index} className={styles.loadSubRow}>
                    {columns.map((col) => (
                        <div key={col.key} className={col.key === 'miles' ? styles.loadMilesColumn : undefined}>{renderTruckLoadCell(load, col)}</div>
                    ))}
                </div>
            ))}
        </div>
    );
};
class WeeklyTruckGrossReport{
    truckNumber;
    totalGross;
    mileage;
    adjustment;
    activeDays;
    rpm;
    truckType;
    availableDays;
    loadCount;
    constructor(report){
        this.truckNumber = report.truckNumber;
        this.totalGross = `$${report.totalGross}`;
        this.mileage = report.coveredMileage && report.coveredMileage > 0 ? `${report.coveredMileage.toLocaleString('en-US')}` :"";
        this.adjustment = report.adjustmentAmount || '';
        this.activeDays = report.activeDays;
        this.rpm = report.coveredMileage || 0;
        this.rpm += report.adjustmentAmount || 0;
        this.rpm = this.rpm === 0 ? '-' : `${(report.totalGross / this.rpm).toFixed(2)}`
        this.truckType = Array.isArray(report.truckType) ? report.truckType.join(', ') : report.truckType;
        this.availableDays = report.availableDays != null ? report.availableDays : 7;
        this.loadCount = report.loads.length;
    }
}

function updateWeeklyTruckRecord(data, selectedPeriod, setCurrentReport, fieldName){
    const fieldConfig = {
        adjustmentAmount: {
            label: "Mileage Adjustment",
            formTitle: "Adjust Mileage",
            parseValue: (value) => parseInt(value, 10) || 0,
            filter: (val) => val.replace(/[^0-9-]/g, ""),
        },
        activeDays: {
            label: "Active Days",
            formTitle: "Adjust Active Days",
            parseValue: (value) => parseFloat(value) || 0,
            filter: (val) => {
                const sanitized = val.replace(/[^0-9.]/g, "");
                const [wholePart, ...decimalParts] = sanitized.split(".");
                const decimalPart = decimalParts.join("").slice(0, 2);
                const normalized = decimalParts.length ? `${wholePart}.${decimalPart}` : wholePart;
                const numberValue = parseFloat(normalized);
                if (Number.isNaN(numberValue)) return normalized;
                return numberValue > 7 ? "7" : normalized;
            },
            validate: (value) => {
                const parsed = parseFloat(value);
                return !Number.isNaN(parsed) && parsed >= 0 && parsed <= 7;
            },
        },
        availableDays: {
            label: "Available Days",
            formTitle: "Adjust Available Days",
            parseValue: (value) => parseFloat(value) || 0,
            filter: (val) => {
                const sanitized = val.replace(/[^0-9.]/g, "");
                const [wholePart, ...decimalParts] = sanitized.split(".");
                const decimalPart = decimalParts.join("").slice(0, 2);
                const normalized = decimalParts.length ? `${wholePart}.${decimalPart}` : wholePart;
                const numberValue = parseFloat(normalized);
                if (Number.isNaN(numberValue)) return normalized;
                return numberValue > 7 ? "7" : normalized;
            },
            validate: (value) => {
                const parsed = parseFloat(value);
                return !Number.isNaN(parsed) && parsed >= 0 && parsed <= 7;
            },
        },
    }[fieldName];

    if (!fieldConfig) return;

    const InputFormData = {columns: [
       {
            label: fieldConfig.label,
            name: fieldName,
            autoComplete:"off",
            filters:[fieldConfig.filter],
            defaultValue : data[fieldName] !== null && data[fieldName] !== undefined ? ()=>{
                return data[fieldName]
            }:null,
        }
    ]}
    addPopup({
        content:CustomAddForm,
        popupData:{
            action:(event, postResultAction)=>{
                event.preventDefault();
                const target = event.target;
                const rawValue = target[fieldName].value;
                if(fieldConfig.validate && !fieldConfig.validate(rawValue)){
                    postResultAction(false);
                    addNotification({message:`${fieldConfig.label} must be between 0 and 7`, type:'alert'});
                    return;
                }
                const body = {};
                if(data.weeklyRecordId){
                    body["id"] = data.weeklyRecordId;
                }else{
                    const nextDay = new Date(selectedPeriod);
                    nextDay.setDate(nextDay.getDate() + 1);
                    body["truckNumber"] = data.truckNumber;
                    body["date"] = nextDay.toISOString().split("T")[0]
                }
                body[fieldName] = fieldConfig.parseValue(rawValue);
                sendRequest('PATCH', API_ROOT+'trucks/updateWeeklyReport',null,(res)=>{
                    setCurrentReport(prev => {
                        for(const key of Object.keys(res)){
                            if(key === "id"){
                                prev.weeklyRecordId = res[key];
                            }else{
                                prev[key] = res[key];
                            }
                        }
                        return {...prev}
                    });

                    postResultAction(true);
                }, ()=>{
                    postResultAction(false);
                    addNotification({message:`Something went wrong`, type:'alert'});
                }, body);
            },
            name:"Confirm",
            formTitle: fieldConfig.formTitle,
            submitTitle:`Save`,
            method:"GET",
            columns: InputFormData['columns']
        }, 
        EZCloseAllowed:true
    })
}
function exportTruckLoadColumnValue(load, key) {
    if (!load) return '';
    if (key === 'loadNumber') return load.loadNumber ?? '';
    if (key === 'states') {
        return `${extractStateStr(load.startCityState)} - ${extractStateStr(load.endCityState)}`;
    }
    if (key === 'miles') return truckLoadMiles(load).exportValue;
    if (key === 'payout') return Number(load.driverPayoutForThisWeek) || 0;
    if (key === 'rate') return Number(load.payoutForThisWeek) || 0;
    return '';
}

function exportTruckColumnValue(report, key) {
    switch (key) {
        case 'truckNumber':
            return report?.truckNumber ?? '';
        case 'totalGross':
            return Number(report?.totalGross) || 0;
        case 'mileage':
            return odometerMilesOf(report);
        case 'activeDays':
            return report?.activeDays != null ? Number(report.activeDays) : 0;
        case 'rpm': {
            const rpm = grossRpmOf(report);
            return rpm == null ? '-' : rpm;
        }
        case 'truckType':
            return Array.isArray(report?.truckType) ? report.truckType.join(', ') : (report?.truckType ?? '');
        case 'availableDays':
            return report?.availableDays != null ? Number(report.availableDays) : 7;
        case 'loadCount':
            return Array.isArray(report?.loads) ? report.loads.length : 0;
        case 'payoutGross':
            return payoutGrossOf(report);
        case 'payoutLoadRpm': {
            const rpm = payoutLoadRpmOf(report);
            return rpm == null ? 'N/A' : rpm;
        }
        case 'payoutOdoRpm': {
            const rpm = payoutOdoRpmOf(report);
            return rpm == null ? '-' : rpm;
        }
        default:
            return '';
    }
}

const excelNumberFormat = (type) => {
    if (type === 'money' || type === 'rpm') return '#,##0.00';
    if (type === 'miles' || type === 'count') return '#,##0';
    if (type === 'days') return '#,##0.0';
    return null;
};

const excelColumnWidth = (col) => {
    if (col.key === 'truckType') return 18;
    if (col.type === 'miles') return 16;
    if (col.type === 'text') return 14;
    return 14;
};

export async function exportTruckLoadsReportGroupedToXlsx(metadata, data) {
    let filename = `Truck Performance Report ${metadata.fileName.replace(/\//g, '.')}`;
    if(metadata.timeframe === 'monthly'){
        filename += ` ${metadata.startDate.slice(0, 4)}`;
    }
    filename += '.xlsx';
    try {
      const catalog = metadata.catalog || TRUCK_PERFORMANCE_COLUMNS;
      const visibleKeys = orderKeysByCatalog(
        Array.isArray(metadata.visibleColumns) && metadata.visibleColumns.length
          ? metadata.visibleColumns
          : getVisibleReportColumns(TRUCK_PERFORMANCE_KEY, catalog),
        catalog
      );
      const truckColumns = resolveVisibleCatalog(visibleKeys, catalog);
      const loadCatalog = metadata.loadCatalog || TRUCK_PERFORMANCE_LOAD_COLUMNS;
      const loadKeys = orderKeysByCatalog(
        Array.isArray(metadata.loadVisibleColumns) && metadata.loadVisibleColumns.length
          ? metadata.loadVisibleColumns
          : getVisibleReportColumns(TRUCK_PERFORMANCE_LOADS_KEY, loadCatalog),
        loadCatalog
      );
      const loadColumns = resolveVisibleCatalog(loadKeys, loadCatalog);
      const legacyLoads = isLegacyTruckLoadColumns(loadColumns.map((col) => col.key));
      const exportLoadColumns = legacyLoads
        ? loadColumns.filter((col) => col.key === 'loadNumber' || col.key === 'rate')
        : loadColumns;
      const headers = [
        ...truckColumns.map((col) => col.label),
        ...exportLoadColumns.map((col) => col.key === 'loadNumber' && legacyLoads ? 'Load Number' : col.label),
      ];
  
      const rows = [];
  
      for (const group of (data ?? [])) {
        const loads = Array.isArray(group?.loads) && group.loads.length
          ? group.loads
          : [null];
  
        loads.forEach((ld, idx) => {
          const truckFields = idx === 0
            ? truckColumns.map((col) => exportTruckColumnValue(group, col.key))
            : truckColumns.map(() => "");
  
          rows.push([
            ...truckFields,
            ...exportLoadColumns.map((col) => exportTruckLoadColumnValue(ld, col.key)),
          ]);
        });
      }
  
      const aoa = [headers, ...rows];
  
      // Worksheet
      const ws = XLSX.utils.aoa_to_sheet(aoa);
  
      ws["!cols"] = [
        ...truckColumns.map((col) => ({ wch: excelColumnWidth(col) })),
        ...exportLoadColumns.map((col) => ({ wch: col.key === 'loadNumber' ? 18 : col.key === 'states' ? 16 : 14 })),
      ];

      const numberFormats = [
        ...truckColumns.map((col) => excelNumberFormat(col.type)),
        ...exportLoadColumns.map((col) => col.key === 'miles' ? '#,##0' : excelNumberFormat(col.type)),
      ];
      const lastRow = aoa.length;
      for (let r = 2; r <= lastRow; r++) {
        numberFormats.forEach((fmt, c) => {
          if (!fmt) return;
          const ref = XLSX.utils.encode_cell({ r: r - 1, c });
          if (ws[ref] && typeof ws[ref].v === "number") {
            ws[ref].t = "n";
            ws[ref].z = fmt;
          }
        });
      }
  
      // Workbook + download
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Truck Loads");
  
      const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
      const blob = new Blob([wbout], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
  
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
  
      await new Promise(res => setTimeout(res, 100));
      URL.revokeObjectURL(url);
  
      
      return true;
    } catch (err) {
      console.error("Failed to export Truck Loads XLSX:", err);
      return false;
    }
  }